import { Controller } from "@hotwired/stimulus"

/**
 * ViewingRescheduleController - Manages the viewing reschedule form
 *
 * Handles date selection, slot loading, and form submission
 * for the self-service reschedule flow.
 */
export default class extends Controller {
  static targets = [
    "dateButton",
    "dateScroller",
    "slotsContainer",
    "scheduledAtInput",
    "timeButton",
    "selectionDisplay",
    "selectionText",
    "submitButton",
    "form",
    "monthLabel",
    "summaryNewTime"
  ]

  static values = {
    slotsUrl: String,
    selectedDate: String,
    selectedTime: String,
    datesWithAvailability: Array
  }

  connect() {
    // If we have a pre-selected date, load its slots
    if (this.datesWithAvailabilityValue.length > 0) {
      this.selectedDateValue = this.datesWithAvailabilityValue[0]
      this.loadSlotsForDate(this.selectedDateValue)
    }
  }

  // Date selection
  selectDate(event) {
    const button = event.currentTarget
    const date = button.dataset.date

    // Don't allow selection of unavailable dates
    if (button.dataset.available === "false" || button.disabled) {
      return
    }

    this.selectedDateValue = date

    // Clear time selection when date changes
    this.selectedTimeValue = ""
    if (this.hasScheduledAtInputTarget) {
      this.scheduledAtInputTarget.value = ""
    }

    // Hide selection display and disable submit
    this.hideSelectionDisplay()
    this.disableSubmit()

    // Update visual state of all date buttons
    this.dateButtonTargets.forEach(btn => {
      const isSelected = btn.dataset.date === date
      const isAvailable = btn.dataset.available === "true"

      // Toggle marker classes
      btn.classList.toggle("date-selected", isSelected)
      btn.classList.toggle("date-available", !isSelected && isAvailable)

      // Toggle style classes for selected state
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)

      // Toggle style classes for available (unselected) state
      btn.classList.toggle("bg-white", !isSelected && isAvailable)
      btn.classList.toggle("border-2", !isSelected && isAvailable)
      btn.classList.toggle("border-gray-200", !isSelected && isAvailable)
      btn.classList.toggle("text-gray-900", !isSelected && isAvailable)

      // Update the day label color (it's the last span - the day name)
      const dayLabel = btn.querySelector("span:last-child")
      if (dayLabel) {
        dayLabel.classList.toggle("text-gray-300", isSelected)
        dayLabel.classList.toggle("text-gray-500", !isSelected)
      }
    })

    // Load slots for selected date
    this.loadSlotsForDate(date)

    // Update month label
    this.updateMonthLabel(date)

    // Scroll to show selected date with some future dates visible
    this.scrollToShowFutureDates(date)
  }

  // Load slots from server
  async loadSlotsForDate(date) {
    if (!this.hasSlotsContainerTarget) return

    // Show loading state
    this.slotsContainerTarget.innerHTML = `
      <div class="text-center py-4 text-gray-500">
        <svg class="animate-spin h-5 w-5 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading times...
      </div>
    `

    try {
      const url = new URL(this.slotsUrlValue, window.location.origin)
      url.searchParams.set("from", date)
      url.searchParams.set("to", date)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to load slots")

      const data = await response.json()
      this.renderSlots(data.slots[date] || [])

    } catch (error) {
      console.error("Error loading slots:", error)
      this.slotsContainerTarget.innerHTML = `
        <div class="text-center py-4 text-red-500">
          Failed to load times. Please try again.
        </div>
      `
    }
  }

  renderSlots(slots) {
    if (!slots || slots.length === 0) {
      this.slotsContainerTarget.innerHTML = `
        <div class="py-8 text-center text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p>No available times for this date</p>
        </div>
      `
      return
    }

    const html = `
      <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        ${slots.map(slot => this.buildSlotButton(slot)).join("")}
      </div>
    `
    this.slotsContainerTarget.innerHTML = html
  }

  buildSlotButton(slot) {
    const hour = parseInt(slot.time.split(":")[0], 10)
    const iconClass = hour < 12 ? "ph ph-sun-horizon" : "ph ph-sun"

    return `
      <button type="button"
              class="slot-button flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg
                     border transition-all duration-150 text-sm font-medium
                     border-gray-200 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"
              data-action="click->viewing-reschedule#selectTime"
              data-time="${slot.datetime}"
              data-viewing-reschedule-target="timeButton">
        <i class="${iconClass} text-sm"></i>
        <span>${slot.time}</span>
      </button>
    `
  }

  // Time selection
  selectTime(event) {
    const button = event.currentTarget
    const datetime = button.dataset.time

    this.selectedTimeValue = datetime

    // Update hidden input
    if (this.hasScheduledAtInputTarget) {
      this.scheduledAtInputTarget.value = datetime
    }

    // Update visual state of all time buttons
    this.slotsContainerTarget.querySelectorAll("button").forEach(btn => {
      const isSelected = btn.dataset.time === datetime

      btn.classList.toggle("slot-selected", isSelected)
      btn.classList.toggle("bg-gray-900", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("border-transparent", isSelected)
      btn.classList.toggle("bg-white", !isSelected)
      btn.classList.toggle("text-gray-700", !isSelected)
      btn.classList.toggle("border-gray-200", !isSelected)
    })

    // Show selection display
    this.showSelectionDisplay(datetime)

    // Enable submit button
    this.enableSubmit()
  }

  showSelectionDisplay(datetime) {
    const date = new Date(datetime)
    const formattedDate = date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    })
    const formattedTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    })

    if (this.hasSelectionDisplayTarget) {
      this.selectionDisplayTarget.classList.remove("hidden")

      if (this.hasSelectionTextTarget) {
        this.selectionTextTarget.textContent = `${formattedDate} at ${formattedTime}`
      }
    }

    // Update summary panel on desktop
    if (this.hasSummaryNewTimeTarget) {
      const shortDate = date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short"
      })
      this.summaryNewTimeTarget.textContent = `${shortDate} at ${formattedTime}`
      this.summaryNewTimeTarget.classList.remove("text-gray-600")
      this.summaryNewTimeTarget.classList.add("text-green-600", "font-medium")
    }
  }

  hideSelectionDisplay() {
    if (this.hasSelectionDisplayTarget) {
      this.selectionDisplayTarget.classList.add("hidden")
    }

    // Reset summary panel on desktop
    if (this.hasSummaryNewTimeTarget) {
      this.summaryNewTimeTarget.textContent = "Select a new time"
      this.summaryNewTimeTarget.classList.remove("text-green-600", "font-medium")
      this.summaryNewTimeTarget.classList.add("text-gray-600")
    }
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  updateMonthLabel(dateString) {
    if (!this.hasMonthLabelTarget) return

    const date = new Date(dateString)
    const monthYear = date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric"
    })
    this.monthLabelTarget.textContent = monthYear
  }

  /**
   * Scrolls the date picker so that the selected date is visible with padding on the left.
   */
  scrollToShowFutureDates(date) {
    const button = this.dateButtonTargets.find(btn => btn.dataset.date === date)
    if (button && this.hasDateScrollerTarget) {
      const scroller = this.dateScrollerTarget

      // Get button position relative to scroller's scrollable content
      const scrollerRect = scroller.getBoundingClientRect()
      const buttonRect = button.getBoundingClientRect()
      const currentScroll = scroller.scrollLeft
      const buttonOffset = buttonRect.left - scrollerRect.left + currentScroll

      // Scroll so there's some padding on the left (~20px)
      const targetScroll = Math.max(0, buttonOffset - 20)
      scroller.scrollTo({ left: targetScroll, behavior: "smooth" })
    }
  }
}
